// The screen behind the unsubscribe link of a dailies mail (#/dailies/unsubscribe/<token>). It sits outside the
// studio: the token in the link is the credential, so no sign-in is asked. Looking changes nothing; one button
// stops the mail, and the page then says it is off.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, createApi, defaultBaseUrl } from "../api.ts";
import { createActionLock, createRequestGate } from "../asyncGuards.ts";
import { BRAND } from "../brand.ts";
import { clearUnsubscribeToken } from "../dailies-unsubscribe.ts";
import { unsubscribeApi } from "../data/dailies-api.ts";
import { formatRoute } from "../route.ts";
import { plainErrText } from "./app.ts";
import { Mark } from "./icons.tsx";

type Failed = { s: "failed"; message: string; retry: "look" | "stop"; workspace?: string };
type View =
  | { s: "loading" }
  | { s: "ask"; workspace: string }
  | { s: "stopping"; workspace: string }
  | { s: "done"; workspace: string }
  /** dailies were already off before this visit */
  | { s: "was-off"; workspace: string }
  | { s: "invalid" }
  | Failed;

const SETTINGS = formatRoute({ view: "settings", page: "dailies" });
/** One plain line for the page: a server failure or a dropped connection never shows the server's own words. */
const messageOf = (e: unknown) => plainErrText(e);

export function DailiesUnsubscribe({ token }: { token: string }) {
  const un = useMemo(
    () => unsubscribeApi(createApi({ baseUrl: defaultBaseUrl(), getToken: () => null })),
    [],
  );
  const [view, setView] = useState<View>({ s: "loading" });
  const gate = useRef(createRequestGate());
  const lock = useRef(createActionLock());
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (view.s === "done" || view.s === "was-off" || view.s === "invalid") {
      clearUnsubscribeToken(token);
    }
  }, [view.s, token]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the explicit retry
  useEffect(() => {
    const current = gate.current.begin();
    setView({ s: "loading" });
    un.look(token).then(
      (r) => current() && setView({ s: r.enabled ? "ask" : "was-off", workspace: r.workspace }),
      (e) =>
        current() &&
        setView(
          e instanceof ApiError && (e.status === 404 || e.status === 400)
            ? { s: "invalid" }
            : { s: "failed", message: messageOf(e), retry: "look" },
        ),
    );
    return () => gate.current.invalidate();
  }, [un, token, attempt]);

  const stop = useCallback(
    async (workspace: string) => {
      if (!lock.current.acquire()) return;
      setView({ s: "stopping", workspace });
      try {
        const r = await un.stop(token);
        setView({ s: "done", workspace: r.workspace });
      } catch (e) {
        setView(
          e instanceof ApiError && e.status === 404
            ? { s: "invalid" }
            : { s: "failed", message: messageOf(e), retry: "stop", workspace },
        );
      } finally {
        lock.current.release();
      }
    },
    [un, token],
  );

  const retry = (v: Failed) =>
    v.retry === "stop" && v.workspace ? void stop(v.workspace) : setAttempt((n) => n + 1);

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">
          <Mark />
          <span>{BRAND.name}</span>
        </span>
      </header>
      <main className="invite" id="main" data-testid="dailies-unsubscribe" aria-labelledby="dl-un-title">
        <section className="invite-card" aria-live="polite">
          <p className="invite-eyebrow">Dailies</p>
          <Body view={view} onStop={(w) => void stop(w)} onRetry={retry} />
        </section>
      </main>
    </div>
  );
}

function Title({ children }: { children: string }) {
  return (
    <h1 id="dl-un-title" className="invite-title">
      {children}
    </h1>
  );
}

function Body({
  view,
  onStop,
  onRetry,
}: {
  view: View;
  onStop: (workspace: string) => void;
  onRetry: (v: Failed) => void;
}) {
  switch (view.s) {
    case "loading":
      return (
        <div className="invite-loading" role="status" aria-busy="true">
          <div className="sk-line" style={{ width: "70%" }} />
          <div className="sk-line" style={{ width: "40%" }} />
          <span className="sr-only">Opening your link</span>
        </div>
      );
    case "ask":
    case "stopping":
      return (
        <>
          <Title>{`Stop the dailies mail for ${view.workspace}?`}</Title>
          <div className="invite-actions">
            <button
              type="button"
              className="btn primary"
              disabled={view.s === "stopping"}
              onClick={() => onStop(view.workspace)}
            >
              {view.s === "stopping" ? "Stopping…" : "Stop these mails"}
            </button>
          </div>
          <p className="invite-note">You can turn them on again in your settings.</p>
        </>
      );
    case "done":
      return (
        <>
          <Title>{`Dailies for ${view.workspace} are off.`}</Title>
          <p className="invite-why">No more mail from us about this.</p>
          <p className="invite-note">
            You can turn them on again in your <a href={SETTINGS}>settings</a>.
          </p>
        </>
      );
    case "was-off":
      return (
        <>
          <Title>{`Dailies for ${view.workspace} are already off.`}</Title>
          <p className="invite-note">
            You can turn them on again in your <a href={SETTINGS}>settings</a>.
          </p>
        </>
      );
    case "invalid":
      return (
        <>
          <Title>This link is not valid.</Title>
          <p className="invite-why">It may be mistyped. You can switch dailies off in your settings.</p>
          <div className="invite-actions">
            <a className="btn" href={SETTINGS}>
              Open settings
            </a>
          </div>
        </>
      );
    case "failed":
      // one flat line and Try again: no card inside the card
      return (
        <>
          <Title>Dailies are not stopped yet.</Title>
          <p className="invite-why" role="alert">
            {view.message}
          </p>
          <div className="invite-actions">
            <button type="button" className="btn" onClick={() => onRetry(view)}>
              Try again
            </button>
          </div>
        </>
      );
  }
}
